import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, query, where, Timestamp, addDoc, deleteDoc } from 'firebase/firestore';
import Layout from './Layout';
import { format, differenceInDays, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/authContext';
// REMOVED: Not using shared function - using Dashboard.js's exact logic inline instead

const RemindersByDisciple = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [disciples, setDisciples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // { discipleId_mentorId: { field: value } }
  const [userTimezone, setUserTimezone] = useState('America/New_York'); // Default timezone

  // Get device timezone as fallback
  const getDeviceTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Timezone-aware calculation function (matching DiscipleSettings.js)
  const calculateNextReminderDateWithTimezone = (baseDate, preferredTimeStr, timezone, addDaysCount = 0) => {
    const [hour, minute] = preferredTimeStr.split(":").map(Number);
    
    // Convert base date to user's timezone to get the correct date components
    const dateInTz = toZonedTime(baseDate, timezone);
    
    // Add days if needed
    if (addDaysCount > 0) {
      dateInTz.setDate(dateInTz.getDate() + addDaysCount);
    }
    
    // Create a date object with the desired time components in the user's timezone
    const year = dateInTz.getFullYear();
    const month = dateInTz.getMonth();
    const day = dateInTz.getDate();
    const localDate = new Date(year, month, day, hour, minute, 0);
    
    // Convert from the user's timezone to UTC (matching backend logic)
    return fromZonedTime(localDate, timezone);
  };

  useEffect(() => {
    fetchUserTimezone();
    fetchDisciples();
  }, [currentUser]);

  const fetchUserTimezone = async () => {
    if (!currentUser) {
      setUserTimezone(getDeviceTimezone());
      return;
    }
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserTimezone(userData?.timezone || getDeviceTimezone());
      } else {
        setUserTimezone(getDeviceTimezone());
      }
    } catch (error) {
      console.error('Error fetching user timezone:', error);
      setUserTimezone(getDeviceTimezone());
    }
  };

  const fetchDisciples = async () => {
    setLoading(true);
    try {
      const disciplesRef = collection(db, 'disciples');
      const disciplesSnapshot = await getDocs(disciplesRef);
      const disciplesList = [];

      for (const discipleDoc of disciplesSnapshot.docs) {
        const discipleData = {
          id: discipleDoc.id,
          ...discipleDoc.data(),
        };

        // Fetch mentorDetails sub-collection
        const mentorDetailsRef = collection(doc(db, 'disciples', discipleDoc.id), 'mentorDetails');
        const mentorDetailsSnapshot = await getDocs(mentorDetailsRef);
        
        const mentors = [];
        for (const mentorDoc of mentorDetailsSnapshot.docs) {
          const mentorData = mentorDoc.data();
          const reminderSettings = mentorData.reminderSettings || {};
          
          // USE THE EXACT SAME CALCULATION AS DASHBOARD.JS
          // Copying Dashboard.js's calculation logic exactly to ensure consistency
          const reminderFrequency = reminderSettings.frequency || 7;
          const lastMessageDate = reminderSettings.lastMessageDate || null;
          const nextReminderDate = reminderSettings.nextReminderDate || null;
          
          // Read nextNotificationDateAndTime (same as Dashboard.js)
          let nextNotificationDateAndTime = null;
          try {
            const settingsAny = reminderSettings || {};
            if (settingsAny && typeof settingsAny === 'object' && settingsAny !== null) {
              const notificationDateAndTime = settingsAny['nextNotificationDateAndTime'];
              if (notificationDateAndTime !== undefined && notificationDateAndTime !== null) {
                if (typeof notificationDateAndTime === 'object' && notificationDateAndTime !== null && 'toDate' in notificationDateAndTime) {
                  nextNotificationDateAndTime = notificationDateAndTime.toDate().toISOString();
                } else if (typeof notificationDateAndTime === 'string') {
                  nextNotificationDateAndTime = notificationDateAndTime;
                } else {
                  nextNotificationDateAndTime = String(notificationDateAndTime);
                }
              }
            }
          } catch (error) {
            // Property doesn't exist or can't be accessed - that's okay
          }
          
          // Initialize calculation variables (same as Dashboard.js)
          let daysUntil = null;
          let hasActiveReminder = false;
          let hasSnoozedReminder = false;
          let reminderStatusValue = null;
          let snoozedReminderData = null;
          
          // Helper functions (same as Dashboard.js)
          const safeParseDate = (dateString) => {
            if (!dateString) return null;
            try {
              if (dateString && typeof dateString === 'object' && dateString.toDate) {
                const date = dateString.toDate();
                if (date && !isNaN(date.getTime())) {
                  return date;
                }
                return null;
              }
              if (typeof dateString === 'string') {
                const date = new Date(dateString);
                return isNaN(date.getTime()) ? null : date;
              }
              return null;
            } catch (error) {
              return null;
            }
          };
          
          const calculateDaysBetween = (futureDate, currentDate = new Date()) => {
            const futureNormalized = new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate());
            const currentNormalized = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            return Math.ceil((futureNormalized - currentNormalized) / (1000 * 60 * 60 * 24));
          };
          
          // PRIORITY 1: Check for active reminders in reminderQueue (same as Dashboard.js)
          // BUT: Find user by mentorAppID first (since we don't have currentUser.uid)
          // IMPORTANT: Only check for current reminder status if currentReminderId exists
          // If currentReminderId is null, we're between reminder cycles - no current reminder exists
          const currentReminderId = reminderSettings.currentReminderId;
          
          try {
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(query(usersRef, where('appID', '==', mentorDoc.id)));
            
            if (!usersSnapshot.empty) {
              const userId = usersSnapshot.docs[0].id;
              const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
              
              // Only check reminderQueue if there's a currentReminderId
              // If currentReminderId is null, skip this check (no current reminder exists)
              if (currentReminderId) {
                // Check BOTH archived and non-archived reminders
                // (snoozed reminders might be archived but still have a future snoozedUntil)
                const reminderQueueQuery = query(
                  reminderQueueRef,
                  where('discipleId', '==', discipleDoc.id)
                );
                const reminderQueueSnapshot = await getDocs(reminderQueueQuery);
              
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              
              for (const reminderDoc of reminderQueueSnapshot.docs) {
                const reminderData = reminderDoc.data();
                const reminderStatus = reminderData.reminderStatus;
                const scheduledFor = reminderData.scheduledFor;
                const snoozedUntil = reminderData.snoozedUntil;
                const snoozeCount = reminderData.snoozeCount || 0;
                const completedAt = reminderData.completedAt;
                
                console.log(`[RemindersByDisciple] Checking reminder ${reminderDoc.id} for ${discipleDoc.id}: status=${reminderStatus}, snoozedUntil=${snoozedUntil ? 'exists' : 'null'}, completedAt=${completedAt ? 'exists' : 'null'}`);
                
                // CRITICAL: Check for snoozed reminders FIRST - they take absolute priority
                // Check if snoozedUntil exists AND is in the future (regardless of reminderStatus or archived status)
                // IMPORTANT: Even if reminder is archived, if it has a future snoozedUntil, it's still snoozed
                if (snoozedUntil && !completedAt) {
                  const snoozedUntilDate = safeParseDate(snoozedUntil);
                  if (snoozedUntilDate) {
                    // Check if snooze is still active (snoozedUntil is in the future)
                    if (snoozedUntilDate > now) {
                      hasSnoozedReminder = true;
                      reminderStatusValue = 'snoozed';
                      snoozedReminderData = {
                        snoozedUntil: snoozedUntilDate,
                        snoozeCount: snoozeCount
                      };
                      // Calculate days until snooze expires
                      const daysUntilSnoozeExpires = calculateDaysBetween(snoozedUntilDate, now);
                      daysUntil = daysUntilSnoozeExpires > 0 ? daysUntilSnoozeExpires : 0;
                      console.log(`[RemindersByDisciple] ✅ FOUND SNOOZED REMINDER (archived=${reminderData.archived || false}): snoozedUntil=${snoozedUntilDate.toISOString()}, daysUntil=${daysUntil}, hasSnoozedReminder=${hasSnoozedReminder}`);
                      // DON'T break - continue to check other reminders, but snoozed takes priority
                    }
                  }
                }
                
                // Check for active reminders (sent or scheduled) that are due
                // BUT: Only if we haven't already found a snoozed reminder
                if (!hasSnoozedReminder) {
                  const isActiveStatus = reminderStatus === 'sent' || reminderStatus === 'scheduled' || reminderStatus === 'queued';
                  const isNotCompleted = !completedAt;
                  
                  if (isActiveStatus && isNotCompleted && scheduledFor) {
                    const scheduledDate = safeParseDate(scheduledFor);
                    if (scheduledDate) {
                      const scheduledDay = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
                      // If scheduled for today or in the past, it needs to be sent
                      if (scheduledDay.getTime() <= today.getTime()) {
                        hasActiveReminder = true;
                        // Calculate days difference - allow negative for overdue (only for active reminders)
                        const daysDiff = Math.ceil((scheduledDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        daysUntil = daysDiff < 0 ? daysDiff : 0;
                        reminderStatusValue = reminderStatus;
                        console.log(`[RemindersByDisciple] Found active reminder: daysUntil=${daysUntil}, hasActiveReminder=${hasActiveReminder}`);
                        break; // Found an active reminder, no need to check others
                      }
                    }
                  }
                }
              }
              
              console.log(`[RemindersByDisciple] Final state for ${discipleDoc.id}: hasSnoozedReminder=${hasSnoozedReminder}, hasActiveReminder=${hasActiveReminder}, daysUntil=${daysUntil}`);
              } else {
                console.log(`[RemindersByDisciple] No currentReminderId for ${discipleDoc.id} - skipping reminderQueue check (between reminder cycles)`);
              }
            }
          } catch (reminderQueueError) {
            // If reminderQueue query fails, continue with normal logic
            console.log('Could not check reminderQueue for disciple:', discipleDoc.id, reminderQueueError);
          }
          
          // PRIORITY 3: Use nextNotificationDateAndTime if available (same as Dashboard.js)
          if (!hasActiveReminder && !hasSnoozedReminder && nextNotificationDateAndTime) {
            const parsedNotificationDate = safeParseDate(nextNotificationDateAndTime);
            if (parsedNotificationDate) {
              daysUntil = calculateDaysBetween(parsedNotificationDate);
            }
          }
          // PRIORITY 4: Fallback to nextReminderDate (same as Dashboard.js)
          else if (currentReminderId && !hasActiveReminder && !hasSnoozedReminder && nextReminderDate) {
            const parsedNextDate = safeParseDate(nextReminderDate);
            if (parsedNextDate) {
              daysUntil = calculateDaysBetween(parsedNextDate);
            }
          }
          // PRIORITY 5: Calculate from lastMessageDate + frequency (same as Dashboard.js)
          else if (currentReminderId && !hasActiveReminder && !hasSnoozedReminder && lastMessageDate) {
            const parsedLastDate = safeParseDate(lastMessageDate);
            if (parsedLastDate) {
              const calculatedNextDate = new Date(parsedLastDate);
              calculatedNextDate.setDate(calculatedNextDate.getDate() + reminderFrequency);
              const now = new Date();
              while (calculatedNextDate <= now) {
                calculatedNextDate.setDate(calculatedNextDate.getDate() + reminderFrequency);
              }
              daysUntil = calculateDaysBetween(calculatedNextDate);
            }
          }
          // PRIORITY 6: Brand new disciple (same as Dashboard.js)
          else if (currentReminderId && !hasActiveReminder && !hasSnoozedReminder) {
            daysUntil = 0;
          }
          
          // If currentReminderId is null, we're between reminder cycles
          // Don't calculate status text - there's no current reminder
          if (!currentReminderId) {
            hasSnoozedReminder = false;
            hasActiveReminder = false;
            daysUntil = null;
            reminderStatusValue = null;
            snoozedReminderData = null;
            console.log(`[RemindersByDisciple] No currentReminderId for ${discipleDoc.id} - between reminder cycles, no status to show`);
          }
          
          // Final validation (same as Dashboard.js)
          if (daysUntil === null || isNaN(daysUntil) || !isFinite(daysUntil)) {
            daysUntil = 0;
          }
          
          // Only allow negative days if there's an active reminder (same as Dashboard.js)
          if (daysUntil < 0 && !hasActiveReminder) {
            daysUntil = 0;
          }
          
          // Calculate status text (same as Dashboard.js)
          // BUT: Only show status text if there's actually a CURRENT reminder (currentReminderId exists)
          // If currentReminderId is null, we're between reminder cycles - no current reminder exists
          let currentReminderStatusText = null;
          
          // Only calculate status text if there's a current reminder
          // (currentReminderId was already declared above)
          if (currentReminderId) {
            if (hasSnoozedReminder && snoozedReminderData) {
              const snoozedUntilDate = snoozedReminderData.snoozedUntil;
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              currentReminderStatusText = `Snoozed till ${monthNames[snoozedUntilDate.getMonth()]} ${snoozedUntilDate.getDate()}`;
            } else if (daysUntil !== null) {
              // Use getReminderStatus function (same as Dashboard.js)
              const getReminderStatus = (daysUntil, reminderStatus, hasActiveReminder = false) => {
                if (reminderStatus === 'wait' || reminderStatus === 'snoozed') {
                  return { text: 'Connect tomorrow' };
                }
                if (daysUntil < 0 && hasActiveReminder) {
                  return { text: 'Overdue' };
                }
                if (daysUntil === 0) {
                  return { text: 'Connect today' };
                }
                if (daysUntil === 1) {
                  return { text: 'Connect tomorrow' };
                }
                if (daysUntil === 2) {
                  return { text: 'Connect in 2 days' };
                }
                return { text: `Connect in ${daysUntil} days` };
              };
              const status = getReminderStatus(daysUntil, reminderStatusValue, hasActiveReminder);
              currentReminderStatusText = status.text;
            }
          }
          // If currentReminderId is null, currentReminderStatusText stays null (no status to show)
          
          // Get current reminder status badge (for the "Current Reminder Date and Time" field)
          // Only fetch if currentReminderId exists - if it's null, there's no current reminder
          let currentReminderStatus = null;
          if (currentReminderId) {
            try {
              const usersRef = collection(db, 'users');
              const usersSnapshot = await getDocs(query(usersRef, where('appID', '==', mentorDoc.id)));
              if (!usersSnapshot.empty) {
                const userId = usersSnapshot.docs[0].id;
                const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
                const reminderDoc = await getDoc(doc(reminderQueueRef, currentReminderId));
                if (reminderDoc.exists()) {
                  const reminderData = reminderDoc.data();
                  const reminderStatus = reminderData.reminderStatus;
                  const completedAt = reminderData.completedAt;
                  if (reminderStatus === 'completed' || completedAt) {
                    currentReminderStatus = 'Completed';
                  } else if (reminderStatus === 'snoozed') {
                    currentReminderStatus = 'Snoozed';
                  } else if (reminderStatus === 'sent' && !completedAt) {
                    currentReminderStatus = 'Sent (no action yet)';
                  } else {
                    currentReminderStatus = reminderStatus || 'Unknown';
                  }
                }
              }
            } catch (error) {
              // Continue without status if there's an error
            }
          }
          
          // Get last reminder status badge (for the "Last Reminder Date and Time" field)
          let lastReminderStatus = null;
          const lastReminderId = reminderSettings.lastReminderId;
          if (lastReminderId) {
            try {
              const usersRef = collection(db, 'users');
              const usersSnapshot = await getDocs(query(usersRef, where('appID', '==', mentorDoc.id)));
              if (!usersSnapshot.empty) {
                const userId = usersSnapshot.docs[0].id;
                const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
                const reminderDoc = await getDoc(doc(reminderQueueRef, lastReminderId));
                if (reminderDoc.exists()) {
                  const reminderData = reminderDoc.data();
                  const reminderStatus = reminderData.reminderStatus;
                  const completedAt = reminderData.completedAt;
                  const snoozedUntil = reminderData.snoozedUntil;
                  const archived = reminderData.archived;
                  
                  // Determine status (check completed first, then snoozed, then sent, etc.)
                  if (reminderStatus === 'completed' || completedAt) {
                    lastReminderStatus = 'Completed';
                  } else if (reminderStatus === 'snoozed' || (snoozedUntil && !completedAt)) {
                    lastReminderStatus = 'Snoozed';
                  } else if (reminderStatus === 'sent' && !completedAt) {
                    lastReminderStatus = 'Sent (no action yet)';
                  } else if (reminderStatus === 'cancelled') {
                    lastReminderStatus = 'Cancelled';
                  } else if (reminderStatus === 'not_completed' && !completedAt) {
                    // Handle "not_completed" status - might be snoozed or sent
                    if (snoozedUntil) {
                      lastReminderStatus = 'Snoozed';
                    } else {
                      lastReminderStatus = 'Sent (no action yet)';
                    }
                  } else {
                    // Use the raw status, or "Unknown" if not set
                    lastReminderStatus = reminderStatus || 'Unknown';
                  }
                  
                  console.log(`[RemindersByDisciple] Last reminder ${lastReminderId} status: ${lastReminderStatus} (reminderStatus=${reminderStatus}, completedAt=${completedAt ? 'yes' : 'no'}, snoozedUntil=${snoozedUntil ? 'yes' : 'no'}, archived=${archived})`);
                } else {
                  console.log(`[RemindersByDisciple] Last reminder ${lastReminderId} not found in reminderQueue`);
                }
              }
            } catch (error) {
              // Continue without status if there's an error
              console.error(`Error fetching last reminder status for ${lastReminderId}:`, error);
            }
          }
          
          mentors.push({
            id: mentorDoc.id,
            ...mentorData,
            reminderSettings: reminderSettings,
            currentReminderStatus: currentReminderStatus,
            currentReminderStatusText: currentReminderStatusText, // This now uses the same calculation as Dashboard.js
            lastReminderStatus: lastReminderStatus, // Status of the last reminder
          });
        }

        if (mentors.length > 0) {
          disciplesList.push({
            ...discipleData,
            mentors,
          });
        }
      }

      setDisciples(disciplesList);
    } catch (error) {
      console.error('Error fetching disciples:', error);
      toast.error('Error loading disciples');
    } finally {
      setLoading(false);
    }
  };

  // Calculate reminder status text (matching mobile app logic)
  const calculateReminderStatusText = (dateValue) => {
    if (!dateValue) return null;
    
    try {
      let reminderDate;
      if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
        reminderDate = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        try {
          reminderDate = parseISO(dateValue);
          if (isNaN(reminderDate.getTime())) {
            reminderDate = new Date(dateValue);
          }
        } catch {
          reminderDate = new Date(dateValue);
        }
      } else {
        reminderDate = new Date(dateValue);
      }

      if (isNaN(reminderDate.getTime())) {
        return null;
      }

      // Normalize dates to midnight for comparison
      const reminderYear = reminderDate.getFullYear();
      const reminderMonth = reminderDate.getMonth();
      const reminderDay = reminderDate.getDate();
      const reminderDateNormalized = new Date(reminderYear, reminderMonth, reminderDay);

      const now = new Date();
      const todayYear = now.getFullYear();
      const todayMonth = now.getMonth();
      const todayDay = now.getDate();
      const today = new Date(todayYear, todayMonth, todayDay);

      // Check for overdue (past dates that are NOT today)
      if (reminderDateNormalized < today) {
        return 'Overdue';
      }

      // If reminder date is today
      if (reminderDateNormalized.getTime() === today.getTime()) {
        return 'Connect today';
      }

      // If reminder date is tomorrow
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (reminderDateNormalized.getTime() === tomorrow.getTime()) {
        return 'Connect tomorrow';
      }

      // Calculate days until
      const daysUntil = differenceInDays(reminderDateNormalized, today);

      if (daysUntil === 2) {
        return 'Connect in 2 days';
      }

      return `Connect in ${daysUntil} days`;
    } catch (error) {
      console.error('Error calculating reminder status text:', error);
      return null;
    }
  };

  const formatDate = (dateValue, formatStr = 'MMM d, yyyy - h:mm a') => {
    if (!dateValue) return 'N/A';
    
    try {
      let date;
      if (dateValue?.toDate) {
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return 'Invalid date';
      }

      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return format(date, formatStr);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const formatDateTimeForInput = (dateValue) => {
    if (!dateValue) return '';
    
    try {
      let date;
      if (dateValue?.toDate) {
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return '';
      }

      if (isNaN(date.getTime())) {
        return '';
      }

      // Convert UTC date to user's timezone for display in datetime-local input
      // datetime-local inputs expect local time, not UTC
      const dateInTz = toZonedTime(date, userTimezone);
      
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      const year = dateInTz.getFullYear();
      const month = String(dateInTz.getMonth() + 1).padStart(2, '0');
      const day = String(dateInTz.getDate()).padStart(2, '0');
      const hours = String(dateInTz.getHours()).padStart(2, '0');
      const minutes = String(dateInTz.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  const handleEditField = (discipleId, mentorId, field, value) => {
    const key = `${discipleId}_${mentorId}`;
    setEditing(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleSaveField = async (discipleId, mentorId, field) => {
    const key = `${discipleId}_${mentorId}`;
    const newValue = editing[key]?.[field];
    
    if (newValue === undefined || newValue === '') {
      toast.error('Please enter a valid date and time');
      return;
    }

    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorId);
      
      // Get current reminderSettings to access preferredTime
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);
      const currentData = mentorDetailsDoc.exists() ? mentorDetailsDoc.data() : {};
      const reminderSettings = currentData?.reminderSettings || {};
      const preferredTime = reminderSettings.preferredTime || '09:00';
      
      // Convert datetime-local string to Date
      const date = new Date(newValue);
      if (isNaN(date.getTime())) {
        toast.error('Invalid date format');
        return;
      }

      const updateData = {};

      if (field === 'nextReminderDate') {
        // When updating nextReminderDate, also calculate and update nextNotificationDateAndTime
        // nextNotificationDateAndTime = nextReminderDate + preferredTime (same date, time from preferredTime)
        const nextNotificationDateAndTime = calculateNextReminderDateWithTimezone(
          date,
          preferredTime,
          userTimezone,
          0 // Don't add days, just apply the preferredTime
        );

        updateData['reminderSettings.nextReminderDate'] = date.toISOString();
        updateData['reminderSettings.nextNotificationDateAndTime'] = nextNotificationDateAndTime.toISOString();
        
        console.log(`[RemindersByDisciple] Updated nextReminderDate to ${date.toISOString()}, calculated nextNotificationDateAndTime to ${nextNotificationDateAndTime.toISOString()} with preferredTime ${preferredTime} in timezone ${userTimezone}`);
        
        // CRITICAL: Create a reminder in reminderQueue if nextNotificationDateAndTime is today or in the past
        // This ensures the reminder is immediately available for processReminders to send
        const now = new Date();
        if (nextNotificationDateAndTime <= now || nextNotificationDateAndTime <= new Date(now.getTime() + 5 * 60 * 1000)) {
          // Fetch disciple data for name
          const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
          const discipleData = discipleDoc.exists() ? discipleDoc.data() : {};
          const discipleName = discipleData.name || 'Unknown Disciple';
          
          // Find user by mentorAppID (mentorId)
          const usersRef = collection(db, 'users');
          const usersSnapshot = await getDocs(query(usersRef, where('appID', '==', mentorId)));
          
          if (!usersSnapshot.empty) {
            const userId = usersSnapshot.docs[0].id;
            const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
            
            // Check if there's already an active reminder for this disciple
            const existingReminders = await getDocs(
              query(reminderQueueRef, where('discipleId', '==', discipleId), where('archived', '==', false))
            );
            
            // Archive any existing active reminders (they're being replaced)
            for (const existingReminder of existingReminders.docs) {
              const existingData = existingReminder.data();
              // Only archive if it's not completed
              if (!existingData.completedAt && existingData.reminderStatus !== 'completed') {
                await updateDoc(doc(reminderQueueRef, existingReminder.id), {
                  archived: true,
                  reminderStatus: 'cancelled',
                  completionReason: 'replaced_by_manual_edit'
                });
                console.log(`[RemindersByDisciple] Archived existing reminder ${existingReminder.id} for ${discipleId}`);
              }
            }
            
            // Create new reminder
            const scheduledFor = nextNotificationDateAndTime <= now 
              ? Timestamp.fromDate(new Date(now.getTime() + 60 * 1000)) // 1 minute from now if in past
              : Timestamp.fromDate(nextNotificationDateAndTime);
            
            const newReminder = {
              discipleId: discipleId,
              discipleName: discipleName,
              mentorAppID: mentorId,
              message: `Time to connect with ${discipleName}`,
              scheduledFor: scheduledFor,
              createdAt: Timestamp.now(),
              reminderStatus: 'queued',
              queueStatus: 'queued',
              archived: false,
              frequency_at_creation: reminderSettings.frequency || 7,
              preferredTime_at_creation: preferredTime,
            };
            
            const reminderDocRef = await addDoc(reminderQueueRef, newReminder);
            
            // Update currentReminderId in mentorDetails
            updateData['reminderSettings.currentReminderId'] = reminderDocRef.id;
            updateData['reminderSettings.currentReminderDateAndTime'] = scheduledFor.toDate().toISOString();
            
            console.log(`[RemindersByDisciple] Created new reminder ${reminderDocRef.id} in reminderQueue for ${discipleId}, scheduled for ${scheduledFor.toDate().toISOString()}`);
            toast.success('Reminder created in queue and will be sent within 5 minutes');
          } else {
            console.warn(`[RemindersByDisciple] Could not find user with appID ${mentorId} to create reminder`);
            toast.error('Could not find user to create reminder');
          }
        } else {
          console.log(`[RemindersByDisciple] nextNotificationDateAndTime is in the future (${nextNotificationDateAndTime.toISOString()}), reminder will be created by createQueuedReminders`);
        }
      } else if (field === 'nextNotificationDateAndTime') {
        // When updating nextNotificationDateAndTime directly, save it as-is (don't recalculate)
        // The datetime-local input gives us a value like "2026-01-08T09:30" which is in local time
        // We need to parse it and convert from user's timezone to UTC
        // IMPORTANT: Do NOT update nextReminderDate - allow manual control for testing
        const [datePart, timePart] = newValue.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        // Create a date object in the user's timezone
        const localDate = new Date(year, month - 1, day, hours, minutes, 0);
        
        // Convert from user's timezone to UTC
        const utcDate = fromZonedTime(localDate, userTimezone);
        
        updateData['reminderSettings.nextNotificationDateAndTime'] = utcDate.toISOString();
        
        // DO NOT update nextReminderDate - allow manual control for testing purposes
        // User can edit nextReminderDate separately if needed
        
        console.log(`[RemindersByDisciple] Updated nextNotificationDateAndTime directly to ${utcDate.toISOString()} (from local input: ${newValue} in timezone ${userTimezone}). nextReminderDate NOT updated - manual control preserved.`);
        
        // CRITICAL: Create a reminder in reminderQueue if nextNotificationDateAndTime is today or in the past
        // This ensures the reminder is immediately available for processReminders to send
        const now = new Date();
        if (utcDate <= now || utcDate <= new Date(now.getTime() + 5 * 60 * 1000)) {
          // Fetch disciple data for name
          const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
          const discipleData = discipleDoc.exists() ? discipleDoc.data() : {};
          const discipleName = discipleData.name || 'Unknown Disciple';
          
          // Find user by mentorAppID (mentorId)
          const usersRef = collection(db, 'users');
          const usersSnapshot = await getDocs(query(usersRef, where('appID', '==', mentorId)));
          
          if (!usersSnapshot.empty) {
            const userId = usersSnapshot.docs[0].id;
            const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
            
            // Check if there's already an active reminder for this disciple
            const existingReminders = await getDocs(
              query(reminderQueueRef, where('discipleId', '==', discipleId), where('archived', '==', false))
            );
            
            // Archive any existing active reminders (they're being replaced)
            for (const existingReminder of existingReminders.docs) {
              const existingData = existingReminder.data();
              // Only archive if it's not completed
              if (!existingData.completedAt && existingData.reminderStatus !== 'completed') {
                await updateDoc(doc(reminderQueueRef, existingReminder.id), {
                  archived: true,
                  reminderStatus: 'cancelled',
                  completionReason: 'replaced_by_manual_edit'
                });
                console.log(`[RemindersByDisciple] Archived existing reminder ${existingReminder.id} for ${discipleId}`);
              }
            }
            
            // Create new reminder
            const scheduledFor = utcDate <= now 
              ? Timestamp.fromDate(new Date(now.getTime() + 60 * 1000)) // 1 minute from now if in past
              : Timestamp.fromDate(utcDate);
            
            const newReminder = {
              discipleId: discipleId,
              discipleName: discipleName,
              mentorAppID: mentorId,
              message: `Time to connect with ${discipleName}`,
              scheduledFor: scheduledFor,
              createdAt: Timestamp.now(),
              reminderStatus: 'queued',
              queueStatus: 'queued',
              archived: false,
              frequency_at_creation: reminderSettings.frequency || 7,
              preferredTime_at_creation: preferredTime,
            };
            
            const reminderDocRef = await addDoc(reminderQueueRef, newReminder);
            
            // Update currentReminderId in mentorDetails
            updateData['reminderSettings.currentReminderId'] = reminderDocRef.id;
            updateData['reminderSettings.currentReminderDateAndTime'] = scheduledFor.toDate().toISOString();
            
            console.log(`[RemindersByDisciple] Created new reminder ${reminderDocRef.id} in reminderQueue for ${discipleId}, scheduled for ${scheduledFor.toDate().toISOString()}`);
            toast.success('Reminder created in queue and will be sent within 5 minutes');
          } else {
            console.warn(`[RemindersByDisciple] Could not find user with appID ${mentorId} to create reminder`);
            toast.error('Could not find user to create reminder');
          }
        } else {
          console.log(`[RemindersByDisciple] nextNotificationDateAndTime is in the future (${utcDate.toISOString()}), reminder will be created by createQueuedReminders`);
        }
      } else {
        // For other fields, just update directly
        updateData[`reminderSettings.${field}`] = date.toISOString();
      }

      await updateDoc(mentorDetailsRef, updateData);
      
      toast.success(`Updated ${field} successfully${field === 'nextReminderDate' ? ' (and recalculated next notification date)' : ''}`);
      
      // Clear editing state
      setEditing(prev => {
        const newEditing = { ...prev };
        if (newEditing[key]) {
          delete newEditing[key][field];
          if (Object.keys(newEditing[key]).length === 0) {
            delete newEditing[key];
          }
        }
        return newEditing;
      });

      // Refresh data
      fetchDisciples();
    } catch (error) {
      console.error('Error saving field:', error);
      toast.error(`Error saving ${field}`);
    }
  };

  const getNotificationMethod = (reminderSettings) => {
    const notificationMethod = reminderSettings.notificationMethod || 'email';
    const enabled = reminderSettings.enabled !== false;
    
    if (!enabled) return 'Notifications Disabled';
    
    if (notificationMethod === 'fcm' || notificationMethod === 'push') {
      return 'Push Notifications';
    } else if (notificationMethod === 'email') {
      return 'Email';
    } else if (notificationMethod === 'both') {
      return 'Push + Email';
    }
    
    return 'Email (default)';
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Reminders by Disciple</h1>
            <p className="text-gray-600 mt-2">View and edit reminder and notification dates for all disciples</p>
          </div>
          <button
            onClick={() => navigate('/app-admin')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Back to Admin
          </button>
        </div>

        {disciples.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No disciples with mentors found</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {disciples.map((disciple) =>
              disciple.mentors.map((mentor) => {
                const key = `${disciple.id}_${mentor.id}`;
                const reminderSettings = mentor.reminderSettings || {};
                const isEditingNextReminder = editing[key]?.nextReminderDate !== undefined;
                const isEditingNextNotification = editing[key]?.nextNotificationDateAndTime !== undefined;

                return (
                  <div key={key} className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-xl font-bold text-gray-800">{disciple.name}</h2>
                        <span className="text-sm text-gray-600">
                          Frequency: {reminderSettings.frequency || 'N/A'} days
                        </span>
                        <span className="text-sm text-gray-600">
                          Preferred Time: {reminderSettings.preferredTime || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-sm text-gray-500">discID: {disciple.id}</p>
                        <p className="text-sm text-gray-500">Mentor ID: {mentor.id}</p>
                      </div>
                    </div>

                    {/* Notification Settings */}
                    <div className="mb-4 p-3 bg-blue-50 rounded">
                      <p className="text-sm font-medium text-gray-700">
                        Notifications: <span className="font-semibold">{getNotificationMethod(reminderSettings)}</span>
                      </p>
                    </div>

                    {/* Last Reminder Date and Time */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        a) Last Reminder Date and Time:
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          {formatDate(reminderSettings.lastReminderDateAndTime || reminderSettings.lastReminderSent)}
                        </div>
                        {mentor.lastReminderStatus && (
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            mentor.lastReminderStatus === 'Completed' 
                              ? 'bg-green-100 text-green-800'
                              : mentor.lastReminderStatus === 'Snoozed'
                              ? 'bg-yellow-100 text-yellow-800'
                              : mentor.lastReminderStatus === 'Sent (no action yet)'
                              ? 'bg-blue-100 text-blue-800'
                              : mentor.lastReminderStatus === 'Cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {mentor.lastReminderStatus}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Last Notification Date and Time */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        b) Last Notification Date and Time:
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {formatDate(reminderSettings.lastNotificationDateAndTime, 'MMM d, yyyy - h:mm a')}
                      </div>
                    </div>

                    {/* Current Reminder Date and Time */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        c) Current Reminder Date and Time:
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          {formatDate(reminderSettings.currentReminderDateAndTime)}
                        </div>
                        <div className="flex items-center gap-2">
                          {mentor.currentReminderStatus && (
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              mentor.currentReminderStatus === 'Completed' 
                                ? 'bg-green-100 text-green-800'
                                : mentor.currentReminderStatus === 'Snoozed'
                                ? 'bg-yellow-100 text-yellow-800'
                                : mentor.currentReminderStatus === 'Sent (no action yet)'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {mentor.currentReminderStatus}
                            </span>
                          )}
                          {mentor.currentReminderStatusText && (
                            <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
                              {mentor.currentReminderStatusText}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Next Reminder Date and Time (Editable) */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        d) Next Reminder Date and Time:
                      </label>
                      {isEditingNextReminder ? (
                        <div className="flex gap-2">
                          <input
                            type="datetime-local"
                            value={editing[key].nextReminderDate || formatDateTimeForInput(reminderSettings.nextReminderDate) || ''}
                            onChange={(e) => handleEditField(disciple.id, mentor.id, 'nextReminderDate', e.target.value)}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <button
                            onClick={() => handleSaveField(disciple.id, mentor.id, 'nextReminderDate')}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditing(prev => {
                                const newEditing = { ...prev };
                                if (newEditing[key]) {
                                  delete newEditing[key].nextReminderDate;
                                  if (Object.keys(newEditing[key]).length === 0) {
                                    delete newEditing[key];
                                  }
                                }
                                return newEditing;
                              });
                            }}
                            className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                            {formatDate(reminderSettings.nextReminderDate)}
                          </div>
                          <button
                            onClick={() => handleEditField(disciple.id, mentor.id, 'nextReminderDate', formatDateTimeForInput(reminderSettings.nextReminderDate))}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Note: Updating this will automatically recalculate the Next Notification Date and Time
                      </p>
                    </div>

                    {/* Next Notification Date and Time (Editable) */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        e) Next Notification Date and Time:
                      </label>
                      {isEditingNextNotification ? (
                        <div className="flex gap-2">
                          <input
                            type="datetime-local"
                            value={editing[key].nextNotificationDateAndTime || formatDateTimeForInput(reminderSettings.nextNotificationDateAndTime) || ''}
                            onChange={(e) => handleEditField(disciple.id, mentor.id, 'nextNotificationDateAndTime', e.target.value)}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <button
                            onClick={() => handleSaveField(disciple.id, mentor.id, 'nextNotificationDateAndTime')}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditing(prev => {
                                const newEditing = { ...prev };
                                if (newEditing[key]) {
                                  delete newEditing[key].nextNotificationDateAndTime;
                                  if (Object.keys(newEditing[key]).length === 0) {
                                    delete newEditing[key];
                                  }
                                }
                                return newEditing;
                              });
                            }}
                            className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                            {formatDate(reminderSettings.nextNotificationDateAndTime)}
                          </div>
                          <button
                            onClick={() => handleEditField(disciple.id, mentor.id, 'nextNotificationDateAndTime', formatDateTimeForInput(reminderSettings.nextNotificationDateAndTime))}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Can be manually edited. When Next Reminder Date is updated, this is auto-calculated.
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RemindersByDisciple;
